import http from '@/api/http'
import {
  businessServicesPageLimit,
  businessServicesByTypePageLimit,
  clientVisitCountsPageLimit,
} from '@/config/api'
import { cursorParams, type PageMeta } from '@/api/pagination'

export interface BusinessService {
  // One business service occurrence from GET `/business-services` (cursor-paginated list).

  id: string
  // Service id (UUID7).

  date_time: string
  // ISO 8601 datetime string at which this service is scheduled / took place.

  business_service_type_id: string
  // Reference to the parent service type definition (joined client-side against `/business-service-types`).

  client_ids: string[]
  // Ids of clients attending the service. Empty array when no clients are attached.

  performers_ids: string[]
  // Ids of performers running the service.

  group_id?: string | null
  // Id of the client group this session was held for; absent/null for ad-hoc sessions.

  description: string | null
  // Optional free-text description; null when absent.

  price: number
  // Raw service price amount as returned by the backend for this occurrence.
}

interface BusinessServicesResponse extends PageMeta {
  // Paginated list payload from GET `/business-services`.

  items: BusinessService[]
  // Page of services.
}

/**
 * Fetches a page of business services. Same cursor/limit contract as `fetchClients`.
 * @param cursor - Optional; pass `next_cursor` from a previous page for the next page.
 */
export async function fetchBusinessServices(cursor?: number): Promise<BusinessServicesResponse> {
  const { data } = await http.get<BusinessServicesResponse>('/business-services', {
    params: cursorParams(businessServicesPageLimit, cursor),
  })
  if (import.meta.env.DEV) {
    console.log('[fetchBusinessServices] GET /business-services response', data)
  }
  return data
}

export interface CreateBusinessServicePayload {
  // Request body for POST `/business-services`.

  date_time: string
  // ISO 8601 datetime string for the service start.

  business_service_type_id: string
  // Id of the service type definition.

  client_ids: string[]
  // Ids of attending clients (may be empty when only performers attend).

  performers_ids: string[]
  // Ids of performers running the service.

  group_id?: string
  // Id of the client group the session is held for; omit for an ad-hoc session.

  description?: string
  // Omit when blank.

  price?: number
  // Optional price override; omit to use the service type's current price.
}

/**
 * Creates a new business service occurrence via POST `/business-services`.
 */
export async function createBusinessService(
  payload: CreateBusinessServicePayload,
): Promise<BusinessService> {
  const { data } = await http.post<BusinessService>('/business-services', payload)
  if (import.meta.env.DEV) {
    console.log('[createBusinessService] POST /business-services response', data)
  }
  return data
}

export interface UpdateBusinessServicePayload {
  date_time: string
  business_service_type_id: string
  performers_ids: string[]
  client_ids: string[]
  group_id: string | null
  description: string | null
  price: number
}

export async function updateBusinessService(
  id: string,
  payload: UpdateBusinessServicePayload,
): Promise<BusinessService> {
  const { data } = await http.patch<BusinessService>(`/business-services/${id}`, payload)
  if (import.meta.env.DEV)
    console.log(`[updateBusinessService] PATCH /business-services/${id} response`, data)
  return data
}

/**
 * Fetches a single business service occurrence by id for the info card.
 * @param id - Business service id selected in the services table.
 */
export async function fetchBusinessService(id: string): Promise<BusinessService> {
  const { data } = await http.get<BusinessService>(`/business-services/${id}`)
  if (import.meta.env.DEV) {
    console.log(`[fetchBusinessService] GET /business-services/${id} response`, data)
  }
  return data
}

/**
 * Deletes a business service occurrence by id.
 * @param id - Business service id to delete.
 */
export async function deleteBusinessService(id: string): Promise<void> {
  await http.delete(`/business-services/${id}`)
  if (import.meta.env.DEV) {
    console.log(`[deleteBusinessService] DELETE /business-services/${id}`)
  }
}

export interface BusinessServicesByTypeResponse extends PageMeta {
  // Paginated list payload from GET `/business-services/types/{business_service_type_id}`
  // (DESCENDING by `date_time`). Item shape is identical to `BusinessServicesResponse`.

  items: BusinessService[]
  // Page of occurrences for a single service type.
}

/**
 * Fetches a page of business service occurrences for a single service type.
 * Cursor/limit contract mirrors `fetchClientBusinessServices`; callers typically
 * auto-drain pages with `useInfiniteQuery` so the card has the full history to
 * feed the monthly-bucket sparklines.
 *
 * @param typeId - Business service type id whose occurrences to fetch.
 * @param cursor - Optional; pass `next_cursor` from a previous page for the next page.
 */
export async function fetchBusinessServicesByType(
  typeId: string,
  cursor?: number,
): Promise<BusinessServicesByTypeResponse> {
  const { data } = await http.get<BusinessServicesByTypeResponse>(
    `/business-services/types/${typeId}`,
    { params: cursorParams(businessServicesByTypePageLimit, cursor) },
  )
  if (import.meta.env.DEV) {
    console.log(
      `[fetchBusinessServicesByType] GET /business-services/types/${typeId} response`,
      data,
    )
  }
  return data
}

export interface VisitedServiceCount {
  // One (business_service_type_id, price) variant with its visit count, nested inside `ClientVisitCounts`.

  business_service_type_id: string
  // Business service type identifier.

  price: number | null
  // Applied price for the visit; null when no price applied.

  visits_count: number
  // Number of visits for this (business_service_type_id, price) combination.
}

export interface ClientVisitCounts {
  // One client's aggregated visit counts from GET `/business-services/client-visit-counts`.

  client_id: string
  // Client identifier.

  client_name: string
  // Client full name.

  visited_services: VisitedServiceCount[]
  // Per-(business_service_type_id, price) visit counts for this client.
}

interface ClientVisitCountsResponse extends PageMeta {
  // Paginated list payload from GET `/business-services/client-visit-counts`.

  items: ClientVisitCounts[]
  // Page of clients with their visit counts.
}

/**
 * Fetches a page of client visit counts for a period, grouped by
 * (business_service_type_id, price). Same cursor/limit contract as
 * `fetchBusinessServices`; `start`/`end` are ISO calendar dates (`YYYY-MM-DD`),
 * inclusive/exclusive respectively, matching the backend's `dt.date` query params.
 */
export async function fetchClientVisitCountsByPeriod(
  start: string,
  end: string,
  cursor?: number,
): Promise<ClientVisitCountsResponse> {
  const { data } = await http.get<ClientVisitCountsResponse>(
    '/business-services/client-visit-counts',
    { params: { start, end, ...cursorParams(clientVisitCountsPageLimit, cursor) } },
  )
  if (import.meta.env.DEV) {
    console.log(
      '[fetchClientVisitCountsByPeriod] GET /business-services/client-visit-counts response',
      data,
    )
  }
  return data
}
